// Review Center → "Minutes to apply".
//
// Batches of changes derived from meeting minutes, waiting for approval before
// they touch the plan. Approving applies the whole batch, because "these
// minutes are correct, apply them" is the decision people actually make —
// approving fourteen rows one at a time is why approval queues go unused.
//
// Data:
//   GET  /projects/:projectId/minutes/changesets?status=pending
//   POST /projects/:projectId/minutes/changesets/:id/approve | /reject { note? }
//
// Gated by the project's requireProposalApproval flag, the same switch the
// Proposals tab uses — a project without it applies minutes directly and this
// tab simply stays empty.
import { matchesReviewFilter } from "helpers/reviewFilter";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Empty, Input, Modal, Skeleton, Space, Table, Tag, Tooltip, message } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { labelFor } from "helpers/artifactPrefix";
import { useBulkSelection, BulkBar, RowCheck, runBatch } from "components/Coordination/BulkSelect";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

const KIND_COLOR = { issue: "red", checklist: "blue", milestone: "purple", task: "default" };
const OP_COLOR = { create: "green", update: "gold", delete: "volcano" };

const dateStr = (d) => (d ? String(d).slice(0, 10) : "—");

export default function MinutesTab({ projectId, onCountChange, filter, reloadKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [viewing, setViewing] = useState(null); // change set whose source minutes are shown
  const [note, setNote] = useState("");

  const loadedOnce = useRef(false);
  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    if (!loadedOnce.current) setLoading(true);
    try {
      const res = await fetch(
        `${url}/projects/${projectId}/minutes/changesets?status=pending`,
        { headers: auth() }
      );
      const body = await res.json().catch(() => ({}));
      const data = Array.isArray(body?.data) ? body.data : [];
      setRows(data);
      if (onCountChange) onCountChange(data.length);
    } catch {
      setRows([]);
      if (onCountChange) onCountChange(0);
    } finally {
      setLoading(false);
      loadedOnce.current = true;
    }
  // reloadKey: the Review Center header refresh icon.
  }, [projectId, onCountChange, reloadKey]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action, noteText) => {
    setWorking(id);
    try {
      const res = await fetch(`${url}/projects/${projectId}/minutes/changesets/${id}/${action}`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteText || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.err || `HTTP ${res.status}`);
      if (action === "approve") {
        const failed = body.failed?.length || 0;
        message.success(
          failed
            ? `Applied ${body.applied}, ${failed} could not be applied`
            : `Applied ${body.applied} item(s) to the plan`
        );
        // Surface partial failures rather than letting them pass silently —
        // an update whose target no longer exists is worth knowing about.
        if (failed) {
          Modal.info({
            title: "Some items could not be applied",
            content: (
              <ul style={{ paddingLeft: 18 }}>
                {body.failed.map((f, i) => <li key={i}>{f.title} — {f.error}</li>)}
              </ul>
            ),
          });
        }
      } else {
        message.success("Rejected");
      }
      setRows((r) => r.filter((x) => x._id !== id));
      if (onCountChange) onCountChange(Math.max(0, rows.length - 1));
    } catch (e) {
      message.error(e.message);
    } finally {
      setWorking("");
      setRejecting(null);
      setNote("");
    }
  };

  const visible = rows.filter((cs) => matchesReviewFilter(cs, filter));
  const sel = useBulkSelection(visible.map((cs) => String(cs._id)));
  // decide() reports per batch (it has to - an approve can partially
  // apply), so the batch runner only needs to sequence the calls and
  // count the ones that threw.
  const bulk = (ids, action) =>
    runBatch(ids, (id) => decide(id, action), {
      verb: action === "approve" ? "applied" : "rejected",
    });

  if (loading) return <Skeleton active />;
  if (!projectId) return <Alert type="info" showIcon message="Pick a project." />;
  if (!rows.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No minutes waiting to be applied" />;
  }

  return (
    <>
      <Alert
        className="mb-3"
        type="info"
        showIcon
        message="Each batch came from one set of minutes. Approving applies the whole batch to the schedule; the items land against the activity the meeting belongs to."
      />
      <BulkBar
        sel={sel}
        busy={working}
        actions={[
          { key: "approve", label: "Approve & apply selected", primary: true,
            run: (ids) => bulk(ids, "approve") },
          { key: "reject", label: "Reject selected", danger: true,
            run: (ids) => bulk(ids, "reject") },
        ]}
      />
      {visible.map((cs) => (
        <Card
          key={cs._id}
          size="small"
          className="mb-3"
          title={
            <Space wrap>
              <RowCheck sel={sel} id={String(cs._id)} />
              <span>Minutes · {dateStr(cs.meetingDate || cs.createdAt)}</span>
              {cs.sourceFilename ? <Tag>{cs.sourceFilename}</Tag> : <Tag>pasted</Tag>}
              <Tag color="blue">{(cs.operations || []).length} item(s)</Tag>
            </Space>
          }
          extra={
            <Space>
              <Tooltip title="Read the minutes this batch was extracted from">
                <Button size="small" icon={<EyeOutlined />} onClick={() => setViewing(cs)}>
                  View minutes
                </Button>
              </Tooltip>
              <Button
                size="small" type="primary" icon={<CheckOutlined />}
                loading={working === cs._id}
                onClick={() => decide(cs._id, "approve")}
              >
                Approve &amp; apply
              </Button>
              <Button
                size="small" danger icon={<CloseOutlined />}
                onClick={() => { setRejecting(cs._id); setNote(""); }}
              >
                Reject
              </Button>
            </Space>
          }
        >
          {cs.attendees?.length > 0 && (
            <p style={{ color: "#6b7a86", marginBottom: 8 }}>
              Attendees: {cs.attendees.join(", ")}
            </p>
          )}
          <Table
            size="small"
            rowKey={(r, i) => i}
            dataSource={cs.operations || []}
            pagination={false}
            columns={[
              {
                title: "Do", dataIndex: "op", width: 90,
                render: (v) => <Tag color={OP_COLOR[v]}>{v}</Tag>,
              },
              {
                title: "As", dataIndex: "kind", width: 100,
                render: (v) => <Tag color={KIND_COLOR[v]}>{v}</Tag>,
              },
              {
                title: "Title", dataIndex: "title",
                render: (v, r) => (
                  <Tooltip title={r.sourceLine} placement="topLeft">
                    <span>{v}</span>
                  </Tooltip>
                ),
              },
              {
                title: "Ref", dataIndex: "refDisplayId", width: 80,
                render: (v, r) => (v ? <Tag>{labelFor(v, r?.artifactPrefix)}</Tag> : <span style={{ color: "#bbb" }}>—</span>),
              },
              { title: "Owner", dataIndex: "assignee", width: 120, render: (v) => v || "—" },
              { title: "Due", dataIndex: "dueDate", width: 110, render: dateStr },
              { title: "Priority", dataIndex: "priority", width: 90 },
              { title: "Discipline", dataIndex: "discipline", width: 120, render: (v) => v || "—" },
            ]}
          />
        </Card>
      ))}

      <Modal
        open={!!viewing}
        title={viewing ? `Minutes - ${dateStr(viewing.meetingDate || viewing.createdAt)}${viewing.sourceFilename ? ` - ${viewing.sourceFilename}` : ""}` : "Minutes"}
        onCancel={() => setViewing(null)}
        footer={<Button onClick={() => setViewing(null)}>Close</Button>}
        width={760}
      >
        {viewing?.attendees?.length > 0 && (
          <p style={{ color: "#6b7a86" }}>Attendees: {viewing.attendees.join(", ")}</p>
        )}
        {viewing?.sourceText ? (
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, maxHeight: "calc(60 * var(--vh))", overflow: "auto", background: "#f7f9fc", padding: 12, borderRadius: 6 }}>
            {viewing.sourceText}
          </pre>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="The source text was not kept for this batch - the items above are all that was extracted" />
        )}
      </Modal>
      <Modal
        open={!!rejecting}
        title="Reject these minutes"
        okText="Reject"
        okButtonProps={{ danger: true }}
        onOk={() => decide(rejecting, "reject", note)}
        onCancel={() => { setRejecting(null); setNote(""); }}
        confirmLoading={working === rejecting}
      >
        <p>Nothing will be created. The batch is kept so it can be looked at later.</p>
        <Input.TextArea
          rows={3} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Why (optional)"
        />
      </Modal>
    </>
  );
}
